/**
 * Typed client for the console API (`/v1/console/*`).
 *
 * Auth: requests carry `credentials: 'include'` for either Cloudflare Access or Guard Dog's
 * host-only platform cookies. One CSRF-protected, single-flight refresh is attempted after a 401;
 * concurrent view loads therefore cannot race WorkOS refresh-token rotation. In dev, point
 * `VITE_API_BASE` at the API origin.
 */
import type {
  AuditEvent,
  InventoryItem,
  LifecycleEntitlement,
  ManagedVpatchHoldInput,
  Rule,
  SecurityEvent,
  SiteDiagnosisResult,
  SiteSeamReport,
  VulnCatalogEntry,
  VulnFeedState,
  WpMode,
} from '@guard-dog/contracts';
import type {
  ApexStrategy,
  GuardDogCloudResult,
  HostnameReadinessReason,
  NormalizedReadinessHostname,
  ProxyLayer,
  ReadinessEvidenceState,
} from '@guard-dog/contracts/qualification';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

/** A failed API call. `status` 0 means the request never completed (network error). */
export class ApiError extends Error {
  readonly status: number;
  /**
   * Where to start a fresh sign-in, when the API reports one. The API sends this on 401s from
   * platform auth; it is absent under Cloudflare Access (which re-authenticates on reload). Its
   * presence is how the console knows a sign-in flow exists without sniffing the hostname — the
   * same bundle serves both deployments.
   */
  readonly signIn?: string;
  /** The API's machine-readable reason (e.g. `site_limit_reached`), when it sent one. */
  readonly code?: string;
  /** True when the API explained itself — i.e. `message` is its words, not a status-code stub. */
  readonly explained: boolean;
  constructor(
    message: string,
    status: number,
    opts: { signIn?: string; code?: string; explained?: boolean } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.signIn = opts.signIn;
    this.code = opts.code;
    this.explained = opts.explained ?? false;
  }
}

/**
 * Build an ApiError from a failed response, keeping what the API actually said.
 *
 * The API returns `{ error: { code, message } }` — precise, actionable text like "site limit
 * reached". Rendering a bare status code instead ("(403)") throws that away and turns a clear
 * answer into a mystery, so the API's own message becomes the error message when present.
 */
async function errorFrom(label: string, res: Response): Promise<ApiError> {
  let signIn: string | undefined;
  let code: string | undefined;
  let message: string | undefined;
  try {
    const body = (await res.clone().json()) as {
      signIn?: unknown;
      error?: { code?: unknown; message?: unknown };
    };
    if (typeof body?.signIn === 'string') signIn = body.signIn;
    if (typeof body?.error?.code === 'string') code = body.error.code;
    if (typeof body?.error?.message === 'string') message = body.error.message;
  } catch {
    // Non-JSON error body — nothing to learn from it.
  }
  return new ApiError(message ?? `${label} failed (${res.status})`, res.status, {
    signIn,
    code,
    explained: message !== undefined,
  });
}

const CSRF_COOKIE = '__Host-gd_csrf';
let refreshInFlight: Promise<boolean> | undefined;

function cookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const prefix = `${name}=`;
  for (const item of document.cookie.split(';')) {
    const value = item.trim();
    if (value.startsWith(prefix)) {
      try {
        return decodeURIComponent(value.slice(prefix.length));
      } catch {
        return undefined;
      }
    }
  }
  return undefined;
}

async function refreshPlatformSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const csrf = cookie(CSRF_COOKIE);
  if (!csrf) return false;
  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${BASE}/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { accept: 'application/json', 'X-GuardDog-CSRF': csrf },
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      refreshInFlight = undefined;
    }
  })();
  return refreshInFlight;
}

async function fetchWithSession(path: string, init: RequestInit): Promise<Response> {
  const request = () => fetch(`${BASE}${path}`, init);
  const response = await request();
  if (response.status !== 401 || !(await refreshPlatformSession())) return response;
  return request();
}

async function getJson<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetchWithSession(path, {
      method: 'GET',
      credentials: 'include',
      headers: { accept: 'application/json' },
    });
  } catch {
    throw new ApiError('network error', 0);
  }
  if (!res.ok) throw await errorFrom(`GET ${path}`, res);
  return (await res.json()) as T;
}

async function sendJson<T>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body: unknown,
): Promise<T> {
  let res: Response;
  try {
    res = await fetchWithSession(path, {
      method,
      credentials: 'include',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError('network error', 0);
  }
  if (!res.ok) throw await errorFrom(`${method} ${path}`, res);
  return (await res.json()) as T;
}

export type EdgeMode = 'enforce' | 'monitor' | 'bypass';

export interface HostnameQualificationInput {
  hostname: string;
  origin?: string;
  dnsControl?: ReadinessEvidenceState;
  apexStrategy?: ApexStrategy;
  replacementHostname?: string;
  originServesSite?: ReadinessEvidenceState;
  proxyLayer?: ProxyLayer;
  layeringProfile?: string;
  customerControlsCurrentZone?: ReadinessEvidenceState;
  canRepointExactHostname?: ReadinessEvidenceState;
  directOrigin?: ReadinessEvidenceState;
}

export interface HostnameQualificationReport {
  schemaVersion: number;
  checkedAt: string;
  hostname: NormalizedReadinessHostname;
  guardDog: { available: true };
  guardDogCloud: {
    result: GuardDogCloudResult;
    reasons: HostnameReadinessReason[];
    unlockSteps: HostnameReadinessReason[];
  };
  evidence: {
    dns: {
      control: ReadinessEvidenceState;
      apexStrategy: ApexStrategy;
      replacementHostname: string | null;
      observation: {
        status: 'observed' | 'not_observed' | 'unavailable';
        recordTypes: ('A' | 'AAAA' | 'CNAME')[];
        recordCount: number;
      };
    };
    origin: {
      reachability: 'reachable' | 'unreachable' | 'unknown';
      servesSite: ReadinessEvidenceState;
      probe: {
        attempted: boolean;
        target: string | null;
        status: number | null;
        elapsedMs: number | null;
        redirectPresent: boolean;
        outcome: 'reachable' | 'unreachable' | 'unknown';
        failure: 'timeout' | 'request_failed' | 'dns_changed' | null;
      };
    };
    proxy: {
      layer: ProxyLayer;
      layeringProfile: string | null;
      authority: {
        customerControlsCurrentZone: ReadinessEvidenceState;
        canRepointExactHostname: ReadinessEvidenceState;
        directOrigin: ReadinessEvidenceState;
      };
    };
  };
}

export interface WpModeDelivery {
  status: 'applied' | 'pending';
  via: 'push' | 'scheduled_pull';
  reason: string;
  detail: string;
  observedWpMode?: WpMode;
}

export interface ConsolePrincipal {
  kind: string;
  tenantId: string;
  userId?: string;
  role?: string;
  entitlement?: LifecycleEntitlement | null;
}

/** True for roles allowed to mutate console state (mirrors the API's `isMutator`). */
export function canMutate(role: string | undefined): boolean {
  return role === 'owner' || role === 'admin' || role === 'platform_admin';
}

/** True for the platform operator — the only role that may hold/release managed vpatches. */
export function isPlatformAdmin(role: string | undefined): boolean {
  return role === 'platform_admin';
}

export interface ConsoleSite {
  id: string;
  tenantId: string;
  url: string;
  edgeHostname?: string;
  edgeOrigin?: string;
  edgeMode?: EdgeMode;
  /**
   * What the WordPress plane may do with the rules it is served (ADR-0030). Undefined is not
   * "unset/none" here — it RESOLVES to `monitor`, and every site in the fleet is currently in that
   * state. Read it through `resolveWpMode`, never with `??` and a placeholder.
   */
  wpMode?: WpMode;
  /** Connector build last reported by the site. Undefined = has not reported since this shipped. */
  agentVersion?: string;
  lastSeenAt?: string;
  /**
   * Which core-plugin seams the connector has OBSERVED firing. Undefined = never reported, which is
   * a different fact from "reported, nothing fired" — see `seamState`.
   */
  seams?: SiteSeamReport;
}

export interface EventsResponse {
  events: SecurityEvent[];
  count: number;
  byType: Record<string, number>;
}

export type InventoryRow = InventoryItem & { lastReportedAt?: string };

export interface InventoryResponse {
  items: InventoryRow[];
  count: number;
}

export interface VulnCatalogResponse {
  entries: VulnCatalogEntry[];
  count: number;
  feedState: VulnFeedState | null;
}

const site = (id: string) => `/v1/console/sites/${encodeURIComponent(id)}`;

export const api = {
  me: () => getJson<ConsolePrincipal>('/v1/console/me'),
  logout: async () => {
    const csrf = cookie(CSRF_COOKIE);
    if (!csrf) throw new ApiError('platform session unavailable', 403);
    let res: Response;
    try {
      res = await fetchWithSession('/v1/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: { accept: 'application/json', 'X-GuardDog-CSRF': csrf },
      });
    } catch {
      throw new ApiError('network error', 0);
    }
    if (!res.ok) throw await errorFrom('POST /v1/auth/logout', res);
  },
  sites: () => getJson<{ sites: ConsoleSite[]; count: number }>('/v1/console/sites'),
  site: (id: string) => getJson<{ site: ConsoleSite }>(site(id)),
  events: (siteId: string, limit = 50) =>
    getJson<EventsResponse>(`${site(siteId)}/events?limit=${limit}`),
  inventory: (siteId: string) => getJson<InventoryResponse>(`${site(siteId)}/inventory`),
  /** The two platform-protection signals a site owner sees in wp-admin (edge digest + vpatch coverage). */
  protection: (siteId: string) => getJson<SiteProtection>(`${site(siteId)}/protection`),
  /**
   * Ask the platform why a site is quiet. POST because it causes an outbound request to the
   * customer's origin — it must never fire from a prefetch or a cached link.
   */
  /**
   * The tenant's recent audit trail. Fetched tenant-wide and filtered per site by the caller: audit
   * rows carry no site column, and a `like` over `detail_json` is not a query worth adding for this.
   */
  auditEvents: (limit = 200) =>
    getJson<{ events: AuditEvent[]; count: number }>(`/v1/console/audit-events?limit=${limit}`),
  diagnoseSite: (siteId: string) =>
    sendJson<{ diagnosis: SiteDiagnosisResult }>('POST', `${site(siteId)}/diagnose`, {}),
  /** Open remote-scan findings + when we last looked (ADR-0018 Stage A). */
  findings: (siteId: string) => getJson<FindingsResponse>(`${site(siteId)}/findings`),
  packageDiff: (siteId: string, pkg: string, kind: string) =>
    getJson<PackageDiffResponse>(
      `${site(siteId)}/package-diff?package=${encodeURIComponent(pkg)}&kind=${encodeURIComponent(kind)}`,
    ),
  vulnCatalog: () => getJson<VulnCatalogResponse>('/v1/console/vuln-catalog'),

  /** What is configured, and what silently does not work without it. Operator-only. */
  configCheck: () => getJson<ConfigCheckResponse>('/v1/console/config-check'),

  // ── Threat intel (ADR-0026 increment B) ────────────────────────────────────
  /** IP reputation currently enforced against this tenant. Tokens are never returned. */
  threatIntel: () => getJson<ThreatIntelResponse>('/v1/console/threat-intel'),
  /** The only way to connect a verdict to an address — the platform does not store addresses. */
  lookupIndicator: (ip: string) =>
    sendJson<IndicatorLookup>('POST', '/v1/console/threat-intel/lookup', { ip }),
  /** One-shot clear. May be re-derived next pass if the evidence is still in the window. */
  clearIndicator: (id: string) =>
    sendJson<{ ok: boolean; note: string }>(
      'DELETE',
      `/v1/console/threat-intel/${encodeURIComponent(id)}`,
      {},
    ),
  allowlist: () => getJson<{ entries: AllowlistEntry[] }>('/v1/console/threat-intel/allowlist'),
  addAllowlist: (ip: string, label: string) =>
    sendJson<AllowlistEntry & { note: string }>('POST', '/v1/console/threat-intel/allowlist', {
      ip,
      label,
    }),
  removeAllowlist: (id: string) =>
    sendJson<{ ok: boolean }>(
      'DELETE',
      `/v1/console/threat-intel/allowlist/${encodeURIComponent(id)}`,
      {},
    ),

  /** The VAPID public key, at runtime — one console build serves every environment (ADR-0020). */
  pushKey: () =>
    getJson<{ publicKey: string | null; canGenerate: boolean }>('/v1/console/push/key'),
  /** Operator-only. Refuses to overwrite without `rotate` — see the route. */
  generatePushKeys: () => sendJson<{ publicKey: string }>('POST', '/v1/console/push/keys', {}),
  pushSubscribe: (subscription: unknown, label: string) =>
    sendJson<{ ok: boolean; scope: string }>('POST', '/v1/console/push/subscribe', {
      subscription,
      label,
    }),
  pushUnsubscribe: (endpoint: string) =>
    sendJson<{ ok: boolean; removed: number }>('DELETE', '/v1/console/push/subscribe', {
      endpoint,
    }),

  /** Prove the alert channel actually works (ADR-0019). Operator-only; sends a real notification. */
  sendTestNotification: () =>
    sendJson<NotificationTestResult>('POST', '/v1/console/notifications/test', {}),
  /** G7 audit alerts deliberately bypass push and prove the engine's HTTPS webhook. */
  sendAuditAlertTest: () =>
    sendJson<NotificationTestResult>('POST', '/v1/console/notifications/audit-test', {}),

  /** Delivery health: did recent alerts actually get through? 405 failures once went unseen. */
  recentNotifications: () =>
    getJson<RecentNotificationsResponse>('/v1/console/notifications/recent'),

  /** Where this tenant's alerts go (ADR-0020). Never carries an address — that is a credential. */
  destinations: () =>
    getJson<{ destinations: Destination[] }>('/v1/console/notifications/destinations'),
  /** Adds one only if it ANSWERS: the api probes it first and stores nothing that cannot receive. */
  addDestination: (input: { address: string; label: string; minUrgency: 'page' | 'notice' }) =>
    sendJson<{ id: string; label: string; scope: string; verified: boolean }>(
      'POST',
      '/v1/console/notifications/destinations',
      { channel: 'webhook', ...input },
    ),
  updateDestination: (
    id: string,
    changes: { minUrgency?: 'page' | 'notice'; disabled?: boolean },
  ) => sendJson<{ ok: boolean }>('PATCH', `/v1/console/notifications/destinations/${id}`, changes),
  removeDestination: (id: string) =>
    sendJson<{ ok: boolean }>('DELETE', `/v1/console/notifications/destinations/${id}`, {}),

  /** The managed vpatch pack with each patch's confidence-soak state (ADR-0011 inc. 2d). */
  managedVpatches: () => getJson<ManagedVpatchesResponse>('/v1/console/managed-vpatches'),
  /** Publish one reviewed, monitor-first managed vpatch (platform operator only). */
  publishManagedVpatch: (input: ManagedVpatchPublicationInput) =>
    sendJson<ManagedVpatchPublicationResult>('POST', '/v1/console/managed-vpatches', input),
  /** Hold/update requires a bounded operator note; release does not (platform operator only). */
  setVpatchHold: (id: string, input: ManagedVpatchHoldInput) =>
    sendJson<{ ok: boolean; id: string; held: boolean }>(
      'POST',
      `/v1/console/managed-vpatches/${encodeURIComponent(id)}/hold`,
      input,
    ),

  /** Change a site's enforcement mode. Re-sends the stored hostname/origin (owner/admin). */
  setEdgeMode: (siteId: string, cfg: { hostname: string; origin: string; mode: EdgeMode }) =>
    sendJson<{ ok: boolean; revision: number }>('PUT', `${site(siteId)}/edge`, cfg),

  /**
   * Check whether one exact hostname can use Guard Dog Cloud. Read-only, but POST because the API
   * performs bounded DNS and HTTP observations and must never run from a prefetch.
   */
  qualifyHostname: (siteId: string, input: HostnameQualificationInput) =>
    sendJson<{ report: HostnameQualificationReport }>(
      'POST',
      `${site(siteId)}/qualification`,
      input,
    ),

  /**
   * Change what the WordPress plane may do with its rules (owner/admin; ADR-0030). The desired mode
   * is durable even if the signed push fast path cannot confirm it; scheduled pull is the fallback.
   */
  setSiteWpMode: (siteId: string, mode: WpMode) =>
    sendJson<{ ok: boolean; wpMode: WpMode; delivery: WpModeDelivery }>(
      'PUT',
      `${site(siteId)}/wp-mode`,
      { mode },
    ),

  /** The tenant's authored rules, incl. disabled (the management/kill-switch view). */
  listRules: () => getJson<{ rules: Rule[]; count: number }>('/v1/console/rules'),

  /** Author or update a rule; auto-distributes to affected edge sites (owner/admin). */
  authorRule: (rule: Rule) =>
    sendJson<{ ok: boolean; ruleId: string; distributed: number }>(
      'POST',
      '/v1/console/rules',
      rule,
    ),

  /** Mint a short-lived, single-use connect token for one site (owner/admin; ADR-0010 E2). */
  connectToken: () =>
    sendJson<{ token: string; expiresAt: string }>('POST', '/v1/console/connect-tokens', {}),

  /** Latest published connector release, or null when none is published yet (404). */
  connectorInfo: (): Promise<ConnectorInfo | null> =>
    getJson<ConnectorInfo>('/v1/console/connector').catch((e) => {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }),

  /** Queue a command for the site (owner/admin); the plugin picks it up on its next sync (≤5 min). */
  enqueueCommand: (siteId: string, verb: string, args?: Record<string, unknown>) =>
    sendJson<{ ok: boolean; commandId: string }>('POST', `${site(siteId)}/commands`, {
      verb,
      ...(args ? { args } : {}),
    }),

  /** Recent commands for the site + their lifecycle state. */
  siteCommands: (siteId: string) =>
    getJson<{ commands: SiteCommand[]; count: number }>(`${site(siteId)}/commands`),

  /** Remove a site from the fleet: soft-delete + credential revocation + edge-state cleanup. */
  deleteSite: (siteId: string) => sendJson<{ ok: boolean }>('DELETE', site(siteId), {}),
};

export interface SiteCommand {
  id: string;
  verb: string;
  status: 'queued' | 'delivered' | 'applied' | 'rejected' | 'unsupported' | 'noop';
  detail?: string;
  createdAt: string;
  ackedAt?: string;
}

export interface ConnectorInfo {
  latestVersion: string;
  sha256?: string;
  requires?: string;
  requiresPhp?: string;
  tested?: string;
  publishedAt?: string;
}

export interface SoakStatus {
  eligible: boolean;
  ageDays: number | null;
  confident: boolean;
  soaked: boolean;
  held: boolean;
  promoted: boolean;
}

export interface ManagedVpatchItem {
  id: string;
  cve?: string;
  confidence?: number;
  description?: string;
  planes: ('edge' | 'wp')[];
  target?: { packageType: 'core' | 'plugin' | 'theme'; slug: string; affectedRange?: string };
  /** Effective enforcement action the fleet receives (source is always monitor-first `log`). */
  action: 'log' | 'block';
  soak: SoakStatus;
}

export interface ManagedVpatchesResponse {
  items: ManagedVpatchItem[];
  count: number;
  policy: { soakDays: number; minConfidenceToPromote: number };
}

/** Reviewed publication packet. The API remains the authoritative schema and safety gate. */
export interface ManagedVpatchPublicationInput {
  catalogId: string;
  target: unknown;
  rule: {
    id: string;
    version: number;
    type: string;
    enabled: boolean;
    scope: string;
    action: { kind: string };
    [key: string]: unknown;
  };
  confirm: string;
}

export interface ManagedVpatchPublicationResult {
  ok: boolean;
  catalogId: string;
  ruleId: string;
  version: number;
  action: 'log';
  matchedSites: number;
  distributedSites: number;
}

/** Operator view of what the platform is doing for a site (mirrors the wp-admin annotations). */
export interface SiteProtection {
  /** 7-day edge-decision summary, or null when the site isn't on the hosted edge. */
  edge: {
    mode: EdgeMode;
    windowDays: number;
    total: number;
    byType: Record<string, number>;
  } | null;
  /** Managed-vpatch coverage, or null under the honesty gate (no reported inventory). */
  vpatch: { vulnerabilities: number; cves: string[] } | null;
}

/** A condition the remote scan currently sees on a site. */
export interface Finding {
  id: string;
  severity: 'info' | 'warn' | 'critical';
  category: string;
  signature: string;
  path?: string;
  evidence?: Record<string, unknown>;
  createdAt: string;
}

interface ScanSummary {
  at: string;
  status: string;
  summary: Record<string, unknown> | null;
}

export interface ConfigCheckItem {
  name: string;
  where: 'api' | 'engine' | 'alerts' | 'storage';
  status: 'ok' | 'missing' | 'unknown' | 'not_applicable';
  severity: 'required' | 'recommended';
  /** What breaks without it — the reason this screen is worth reading. */
  consequence: string;
}

export interface ConfigCheckResponse {
  checks: ConfigCheckItem[];
  summary: { requiredMissing: number; recommendedMissing: number; unknown: number };
}

/**
 * Derived IP reputation enforced against this tenant (ADR-0026 increment B).
 *
 * Deliberately carries no indicator value: ADR-0007 tokenizes at ingest and the raw address is never
 * stored, so there is nothing here a human could read as an IP. Use `lookupIndicator` to ask about
 * an address you already have.
 */
export interface ThreatIntelIndicator {
  id: string;
  action: 'block' | 'challenge';
  scope: 'site' | 'tenant' | 'global';
  confidence: number;
  sourceSites: number | null;
  firstSeen: string;
  lastSeen: string;
}

export interface ThreatIntelResponse {
  indicators: ThreatIntelIndicator[];
  counts: { total: number; blocking: number };
}

export interface IndicatorLookup {
  ip: string;
  enforced: boolean;
  action: 'block' | 'challenge' | null;
  confidence: number | null;
}

/** An address a tenant has declared its own. The stored token is never returned — only the label. */
export interface AllowlistEntry {
  id: string;
  label: string;
  indicatorType?: string;
  createdAt: string;
}

/**
 * A configured alert destination (ADR-0020).
 *
 * Carries no address, by design and not by omission: a webhook URL is a bearer credential, so the
 * console is never given one to lose. `label` is authored by whoever added it, which is exactly why
 * it is the thing that can safely be shown in a list.
 */
export interface Destination {
  id: string;
  channel: string;
  label: string;
  minUrgency: 'page' | 'notice';
  scope: 'platform' | 'tenant';
  verified: boolean;
  disabled: boolean;
  createdAt: string;
}

/** Delivery health for the alert pipeline — counts over 7 days plus the last failure's wording. */
export interface RecentNotificationsResponse {
  summary: {
    since: string;
    sent: number;
    failed: number;
    suppressed: number;
    logged: number;
    lastFailure: { title: string; delivery: string | null; at: string } | null;
  };
  notifications: {
    kind: string;
    urgency: string;
    title: string;
    channel: string;
    status: 'sent' | 'suppressed' | 'failed' | 'logged';
    createdAt: string;
    delivery: string | null;
  }[];
}

/** What the engine reports back about its own channel. */
export interface NotificationTestResult {
  sent: number;
  failed: number;
  logged: number;
  suppressed: number;
  /** False = NO transport at all could have carried this. A useful ANSWER, not an error. */
  configured: boolean;
  /** Split out because "the webhook is set" and "push is set" fail in different ways. */
  webhookConfigured?: boolean;
  pushConfigured?: boolean;
  environment: string | null;
  /** Why the send failed, already stripped of the webhook URL (which is itself a credential). */
  lastError?: string;
  /** `webhook` for the G7 audit test; `all` for the general channel test. */
  transport?: 'webhook' | 'all';
}

export interface FindingsResponse {
  findings: Finding[];
  count: number;
  /** Null until the first remote scan pass has run for this site. */
  latestScan: ScanSummary | null;
  /** Null until this site joins the exact DNS-monitoring cohort and its first pass runs. */
  latestDnsScan: ScanSummary | null;
  /** Null until this site joins the exact TLS-monitoring cohort and its first daily pass runs. */
  latestSslScan: ScanSummary | null;
  /** Null until a connector has reported files and the adjudication pass has run (ADR-0018 Stage B). */
  latestFimScan: ScanSummary | null;
  /** What the host last claimed per scope. `truncated` means the picture is incomplete. */
  fimScopes: {
    scope: string;
    /** ACTUAL stored snapshot size — never a chunk's entryCount. */
    files: number;
    truncated: boolean;
    at: string | null;
    /** Mid-cycle: the number above is a snapshot "so far", not a settled total. */
    walkInProgress?: boolean;
  }[];
}

/** The files behind a rolled-up package finding (ADR-0018) — fetched on demand, never inline. */
export interface PackageDiffResponse {
  package: string;
  kind: 'modified' | 'unexpected';
  version: string | null;
  files: { path: string; sha256: string; firstSeenAt: string; size: number }[];
  truncated: boolean;
}
